import FilterChip from 'LuxKit/Chips/FilterChip'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import React, { useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { getTrendingDestinationFilters } from './constants'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TrendingDestinationsCarousel from './TrendingDestinationsCarousel'
import { useHotelTrendingDestinations } from 'home/hooks/useHotelTrendingDestinations'
import TextButton from 'LuxKit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { sortBy } from 'lib/array/arrayUtils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import config from 'constants/config'
import { useIntl, FormattedMessage } from 'react-intl'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

export const DEFAULT_FILTER: App.DestinationSelectedFilters = 'hotel'

interface Props {
  title?: React.ReactNode
  titleVariant?: React.ComponentProps<typeof Heading>['variant']
  defaultFilter?: App.DestinationSelectedFilters
  filterOrder?: Record<App.DestinationSelectedFilters, number>
  hideFilterOptions?: boolean
  hideMapLink?: boolean
}

function TrendingDestinations(props: Props) {
  const {
    title,
    defaultFilter = DEFAULT_FILTER,
    hideFilterOptions,
    hideMapLink,
    titleVariant = 'heading2',
  } = props
  const [selectedFilter, setSelectedFilter] = useState<App.DestinationSelectedFilters>(defaultFilter)
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)

  const sortedFilters = useMemo(() => {
    const filters = getTrendingDestinationFilters(currentRegionCode)
    return sortBy(
      filters.map(filter => {
        let title: string
        if (filter.id === 'hotel') {
          title = intl.formatMessage({
            id: '0X2CUNfCAf',
            defaultMessage: 'Places to stay',
            description: 'Filter title for hotel in trending destinations',
          })
        } else if (filter.id === 'cruise') {
          title = intl.formatMessage({
            id: 'r3TPZAohZv',
            defaultMessage: 'Cruising the seas',
            description: 'Filter title for cruise in trending destinations',
          })
        } else {
          title = intl.formatMessage({
            id: 'w1vU+ywCjZ',
            defaultMessage: 'Guided group tours',
            description: 'Filter title for tour in trending destinations',
          })
        }
        return {
          ...filter,
          title,
        }
      }),
      a => props.filterOrder?.[a.id] ?? Infinity,
      'asc',
    )
  }, [currentRegionCode, props.filterOrder, intl])

  const lereDestinationModelEnabled: boolean = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lereDestinationModelEnabled)
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const leUserId = useAppSelector(getCurrentUserId)
  const searchMapLink = `/search/map?adults=2&children=none&destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}`
  const isHotelFilter = selectedFilter === 'hotel'
  const [displayTrendingDestinations, fetching] = useHotelTrendingDestinations(selectedFilter, currentRegionCode, leUserId, domainUserId, !(lereDestinationModelEnabled && isHotelFilter && config.BRAND == 'luxuryescapes'))

  return (
    <SectionContainer gap={32}>
      <LayoutContainer>
        <VerticalSpacer gap={12}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
            <Heading variant={titleVariant}>
              {title ?? (
                <FormattedMessage
                  id="xAzTT7yDe0"
                  defaultMessage="Immerse yourself in trending <i>destinations</i>"
                  description="Title for trending destinations section with italic 'destinations'"
                  values={{
                    i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                  }}
                />
              )}
            </Heading>
            {!hideMapLink && <CSSBreakpoint min="desktop">
              <TextButton kind="tertiary" size="medium" startIcon={<LineMapIcon />} to={searchMapLink} target="_blank">
                {intl.formatMessage({
                  id: 'ossK22AAhf',
                  defaultMessage: 'View map',
                  description: 'Button text to view destinations on map',
                })}
              </TextButton>
            </CSSBreakpoint>}
          </Group>
          {!hideFilterOptions && <HorizontalContainerWithFadedOverflow gap={4}>
            {sortedFilters.map((filter) => <FilterChip
              selected={filter.id === selectedFilter}
              onClick={() => setSelectedFilter(filter.id)}
              key={filter.id}
              size="medium"
            >
              {filter.title}
            </FilterChip>)}
          </HorizontalContainerWithFadedOverflow>}
        </VerticalSpacer>
      </LayoutContainer>
      <TrendingDestinationsCarousel fetchingDestinations={fetching} selectedFilter={selectedFilter} trendingDestinations={displayTrendingDestinations} />
    </SectionContainer>
  )
}

export default TrendingDestinations
